/* ── Photo attachments: JPEG blobs in the tennis/photos folder ──────────── */

import type { PhotosConfig } from './storage';

/** Rows carry only a file name; the image itself lives in Blob Storage. */
export const PHOTO_NAME_RE = /^[A-Za-z0-9._-]{1,80}\.jpg$/;

export const PHOTO_MAX_BYTES = 500_000;

export function photoUrl(photos: PhotosConfig, name: string): string {
  return `${photos.baseUrl}/${name}?${photos.sas}`;
}

export async function uploadPhoto(photos: PhotosConfig, name: string, blob: Blob): Promise<boolean> {
  try {
    const response = await fetch(photoUrl(photos, name), {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/jpeg' },
      body: blob,
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Downscale and re-encode a picked image until it fits the size budget. */
export async function compressPhoto(file: File): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return null;
  try {
    let dim = 1600;
    let quality = 0.8;
    for (let attempt = 0; attempt < 6; attempt++) {
      const scale = Math.min(1, dim / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (blob && blob.size <= PHOTO_MAX_BYTES) return blob;
      dim = Math.round(dim * 0.8);
      quality = Math.max(0.55, quality - 0.07);
    }
    return null;
  } finally {
    bitmap.close();
  }
}
